import type { ReactNode } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqDetail } from "../../types";
import { budgetLabel } from "../../utils/budget";
import { formatCalendarDate, formatRespondBy } from "../../utils/deadline";
import { describeRfq } from "../../utils/rfqCardCopy";

interface RfqStatStripProps {
  rfq: VendorRfqDetail;
  bidTotal?: number;
  now: number;
  isMobile?: boolean;
}

interface StatProps {
  label: string;
  value: string;
  valueColor?: string;
  testId?: string;
  span?: boolean;
  children?: ReactNode;
}

function Stat({ label, value, valueColor, testId, span = false, children }: StatProps) {
  return (
    <Box sx={{ backgroundColor: "white", border: `1px solid ${V2_COLORS.border}`, borderRadius: "8px", p: 1.5, minWidth: 0, ...(span && { gridColumnStart: 1, gridColumnEnd: -1 }) }}>
      <Typography fontSize="10px" sx={{ color: V2_COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </Typography>
      <Typography data-testid={testId} fontSize="16px" fontWeight={700} sx={{ color: valueColor, mt: 0.5, wordBreak: "break-word" }}>
        {value}
      </Typography>
      {children}
    </Box>
  );
}

export const RESPOND_BY_NOTE = "Within 24 hours of float · after this the RFQ moves to the next vendor in the pool";

export default function RfqStatStrip({ rfq, bidTotal, now, isMobile = false }: RfqStatStripProps) {
  const copy = describeRfq(rfq, now);
  const bidDiffers = bidTotal !== undefined && bidTotal !== rfq.ask_total;

  return (
    <Box data-testid="rfq-stats" sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}>
      <Stat label="RFQ value (ask)" value={formatMoney(rfq.ask_total)} />
      <Stat label="Your bid total" value={bidTotal === undefined ? "—" : formatMoney(bidTotal)} valueColor={bidDiffers ? V2_COLORS.success : undefined} testId="stat-bid-total" />
      <Stat label="Expected delivery" value={formatCalendarDate(rfq.expected_delivery_date)} />
      <Stat label="Site pincode" value={rfq.site_pincode} />
      <Stat label="Project budget" value={budgetLabel(rfq.project_budget) ?? "—"} testId="stat-budget" span={isMobile} />
      <Stat label="Respond by" value={formatRespondBy(rfq.respond_by)} valueColor={V2_COLORS.warning} span={isMobile}>
        <Typography fontSize="11px" sx={{ color: V2_COLORS.textSecondary, mt: 0.5 }}>
          {RESPOND_BY_NOTE}
        </Typography>
        {copy.countdown && (
          <Chip
            size="small"
            label={copy.countdown}
            sx={{
              mt: 1,
              fontWeight: 600,
              color: copy.windowOpen ? V2_COLORS.warning : V2_COLORS.textSecondary,
              backgroundColor: copy.windowOpen ? V2_COLORS.warningBg : V2_COLORS.bgMuted,
            }}
          />
        )}
      </Stat>
    </Box>
  );
}
